/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from 'react';
import Select from '../../../../juristec-ui/core/Select';
import Tooltip from '../../../../juristec-ui/core/Tooltip';
import { Question } from '../../../../juristec-ui/icons';

import {
  HeaderTips,
  SecondPageContainer,
  VarContainer,
  SelectContainer,
  IconWrapper,
} from './styled/BindVariables.styled';

import { ColumnType, Template } from '../types';

const legacyTypes = {
  float: 'float64',
  date: 'datetime64[ns]',
  abc: 'category',
} as const;

interface SelectOption {
  id: string;
  label: string;
  value: string;
  type: typeof legacyTypes[keyof typeof legacyTypes];
}

interface BindVariablesProps {
  selected: Template;
  fileColumns: ColumnType;
  columnVarBinds: Record<string, string>;
  setColumnVarBinds: (
    binds: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)
  ) => void;
}

interface OptionsState {
  abc: SelectOption[];
  float: SelectOption[];
  date: SelectOption[];
}

/**
 * Second step for the 'ApplyTemplate'.
 * This is where the binding between columns and variables are made.
 */
const BindVariables = ({
  selected = { columnsRequired: {}, columnsType: { abc: [], float: [], date: [] } },
  fileColumns,
  columnVarBinds = {},
  setColumnVarBinds,
}: BindVariablesProps) => {
  const [options, setOptions] = useState<OptionsState>({ abc: [], float: [], date: [] });

  useEffect(() => {
    const newOptions = Object.entries(legacyTypes).reduce((acc, [key, type]) => ({
      ...acc,
      [key]: fileColumns[key as keyof typeof legacyTypes].map((col) => ({
        id: col,
        label: col,
        value: col,
        type,
      })),
    }), { abc: [], float: [], date: [] } as OptionsState);
    setOptions(newOptions);
  }, [fileColumns]);

  useEffect(() => {
    const newBinds = { ...columnVarBinds };
    Object.keys(legacyTypes).forEach((key) => {
      const cType = key as keyof typeof legacyTypes;
      if (selected.columnsType?.[cType]?.length > 0) {
        selected.columnsType[cType].forEach((variable) => {
          const matchingColumn = options[cType].find(
            (col) => col.value.toLowerCase() === variable.toLowerCase(),
          );
          if (matchingColumn && !columnVarBinds[variable]) {
            newBinds[variable] = matchingColumn.value;
          }
        });
      }
    });
    if (Object.keys(newBinds).length > Object.keys(columnVarBinds).length) {
      setColumnVarBinds(newBinds);
    }
  }, [options, selected.columnsType]);

  const handleBinding = (variable: string, column: SelectOption | null) => {
    if (column) {
      setColumnVarBinds((old: Record<string, string>) => ({ ...old, [variable]: column.value }));
    } else {
      setColumnVarBinds((old) => {
        const { [variable]: _, ...newObj } = old;
        return newObj;
      });
    }
  };

  const filterOptions = (variable: string, cType: keyof typeof legacyTypes): SelectOption[] => (
    options[cType]?.filter((opt) => (
      !Object.values(columnVarBinds).includes(opt.value) || opt.value === columnVarBinds[variable]
    )) || []
  );

  return (
    <SecondPageContainer>
      <HeaderTips>
        <span>
          Selecione as colunas do seu arquivo correspondentes as variáveis
          necessárias para a aplicação do template.
        </span>
        <span>
          <span className="danger">Observação: </span>
          A associação de variáveis
          que possuem outro significado em seu arquivo, pode gerar indicadores
          errados
        </span>
      </HeaderTips>
      <VarContainer>
        {(Object.keys(legacyTypes) as Array<keyof typeof legacyTypes>).map((cType) => {
          if (selected.columnsType[cType]?.length > 0) {
            return (
              selected.columnsType[cType].sort((a, b) => a.localeCompare(b, 'pt-BR')).map((variable) => (
                <SelectContainer key={variable}>
                  <Tooltip
                    className="description"
                    text={(
                      <div style={{ whiteSpace: 'pre-wrap', maxWidth: '250px' }}>
                        {selected.columnsRequired[variable]?.description as string}
                      </div>
                    )}
                    direction="top"
                    atModal
                  >
                    <IconWrapper>
                      <Question />
                    </IconWrapper>
                  </Tooltip>
                  <Select
                    placement="start"
                    selectLabel={variable}
                    options={filterOptions(variable, cType)}
                    // @ts-expect-error not typed yet
                    value={{
                      id: columnVarBinds[variable],
                      label: columnVarBinds[variable],
                      value: columnVarBinds[variable],
                    }}
                    // @ts-expect-error not typed yet
                    onChange={
                      (
                        columnSelected: SelectOption,
                      ) => handleBinding(variable, columnSelected)
                    }
                    placeholder=""
                    alphabeticalOrder
                    isClearable
                    isSearchable
                    fullWidth
                    atModal
                  />
                </SelectContainer>
              ))
            );
          } return null;
        })}
      </VarContainer>
    </SecondPageContainer>
  );
};

export default BindVariables;
